/**
 * Builds the user-facing message for a failed registration submission.
 *
 * Backend integration failures (e.g. Hockey Canada) come back as a 422 with a
 * field-keyed `errors` object whose key matches no form field:
 *
 *   { message: "...", errors: { hockey_canada: ["...", "Postal Code: Missing parameter"] } }
 *
 * `normalizeApiError` collapses that to `invalid_fields.hockey_canada` keeping
 * only the FIRST message, and no form renders that key — so the detail is lost
 * twice over. This reads the raw `errors` object instead (it survives
 * normalization) and keeps every message, falling back to the top-level
 * `message` and finally to a generic support line.
 */

export const GENERIC_SUBMISSION_ERROR =
  'We were unable to confirm your submission. Please Contact SportNinja Support.'

interface SubmissionError {
  errors?: unknown
  message?: unknown
}

/**
 * Flattens a field-keyed `errors` object into an ordered, de-duplicated list of
 * non-blank messages. Returns an empty array for any other shape (a positional
 * array is a global error, not field validation — see normalizeApiError).
 */
const collectErrorMessages = (errors: unknown): string[] => {
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return []
  }

  const messages: string[] = []

  for (const value of Object.values(errors as Record<string, unknown>)) {
    const entries = Array.isArray(value) ? value : [value]

    for (const entry of entries) {
      if (typeof entry !== 'string') {
        continue
      }

      const trimmed = entry.trim()

      if (trimmed && !messages.includes(trimmed)) {
        messages.push(trimmed)
      }
    }
  }

  return messages
}

/**
 * Resolves the error to its individual messages, preserving them as separate
 * entries so the call site can render them as distinct lines. Always returns at
 * least one entry.
 */
export const getSubmissionErrorMessages = (error: unknown): string[] => {
  const candidate = (error ?? {}) as SubmissionError

  const messages = collectErrorMessages(candidate.errors)

  if (messages.length > 0) {
    return messages
  }

  if (typeof candidate.message === 'string' && candidate.message.trim()) {
    return [candidate.message.trim()]
  }

  return [GENERIC_SUBMISSION_ERROR]
}

/**
 * Newline-joined form of {@link getSubmissionErrorMessages}, for call sites that
 * need a plain string (note that most renderers collapse the newlines).
 */
export const getSubmissionErrorMessage = (error: unknown): string =>
  getSubmissionErrorMessages(error).join('\n')
